import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useFetch } from '@stashinvest/use-fetch';
import { openDialog } from 'stash/actions/dialog';
import { postMicrosBankAccount } from 'stash/actions/api/bankAccount';
import { linkBankMicrosSubmit } from 'stash/actions/linkBank';
import LinkBankMicrosIntro from './LinkBankMicrosIntro';
import LinkBankRestricted, { RESTRICTED_USER_ERROR_CODE } from './LinkBankRestricted';
import LinkBankMicros from './LinkBankMicros';
import { track } from 'stash/hooks/useTrack';
import {
	v1_ExternalAccountLinkSetup_Failed,
	v1_ExternalAccountLinkSetup_Submitted,
	v1_ExternalAccountLinkSetup_Succeeded,
} from '@stashinvest/product-events-javascript/lib/v1/event_static_methods';
import {
	v1_ExternalAccountLinkSetup_FailedProperties,
	v1_ExternalAccountLinkSetup_SubmittedProperties,
	v1_ExternalAccountLinkSetup_SucceededProperties,
} from '@stashinvest/product-events-javascript/lib/v1/properties';

const MAX_RETRIES = 5;
const POLL_INTERVAL = 1000;

export const LinkBankMicrosContainer = ({ useBankLink, complete }) => {
	const [status, setStatus] = useState('intro');
	const [submitting, setSubmitting] = useState(false);

	const { fetch: getManageExternalAccount } = useFetch({
		method: useFetch.GET,
		path: `/apis/externalAccounts/api/v1/users/:uuid/manageExternalBankAccount`,
		throwOnError: true,
	});

	const polling = useRef(null);

	useEffect(() => {
		return () => {
			if (polling.current) clearInterval(polling.current);
		};
	}, []);

	const dispatch = useDispatch();

	const onSubmit = (values) => {
		setSubmitting(() => true);
		const payload = {
			accountType: values.accountType,
			accountNumber: values.accountNumber,
			routingNumber: values.routingNumber,
		};

		return dispatch(postMicrosBankAccount(payload))
			.then(() => {
				dispatch(linkBankMicrosSubmit());
			})
			.then(() => {
				pollManageExternalAccount(values.accountType);
			})
			.catch((e) => {
				setSubmitting(() => false);
				if (e.errors?.[0]?.code === RESTRICTED_USER_ERROR_CODE) {
					return setStatus('restricted');
				} else {
					dispatch(
						openDialog(
							e.displayError || 'Looks like there was a problem. Please try again.',
							'Try again'
						)
					).catch(() => {});
				}
				track(
					v1_ExternalAccountLinkSetup_Failed({
						external_account_connection_type:
							v1_ExternalAccountLinkSetup_FailedProperties?.ExternalAccountConnectionType
								?.microDeposits,
						error_type: v1_ExternalAccountLinkSetup_FailedProperties?.ErrorType?.apiError,
					})
				);
			});
	};

	const pollManageExternalAccount = (accountType) => {
		let counter = 0;
		polling.current = setInterval(() => {
			if (counter < MAX_RETRIES) {
				getManageExternalAccount()
					.then((res) => {
						counter++;
						if (res.manageExternalBankAccount?.bankAccount?.state === 'pending') {
							complete(accountType?.toLowerCase());

							track(
								v1_ExternalAccountLinkSetup_Succeeded({
									external_account_connection_type:
										v1_ExternalAccountLinkSetup_SucceededProperties
											.ExternalAccountConnectionType.microDeposits,
								})
							);

							return clearInterval(polling.current);
						}
					})
					.catch(() => {
						counter++;
					});
			} else {
				return clearInterval(polling.current);
			}
		}, POLL_INTERVAL);
	};

	const handleValidationErrors = useCallback(() => {
		track(
			v1_ExternalAccountLinkSetup_Failed({
				external_account_connection_type:
					v1_ExternalAccountLinkSetup_FailedProperties?.ExternalAccountConnectionType
						?.microDeposits,
				error_type:
					v1_ExternalAccountLinkSetup_FailedProperties?.ErrorType?.validationError,
			})
		);
	}, []);

	const handleOnBeforeValidation = useCallback(() => {
		track(
			v1_ExternalAccountLinkSetup_Submitted({
				external_account_connection_type:
					v1_ExternalAccountLinkSetup_SubmittedProperties?.ExternalAccountConnectionType
						?.microDeposits,
				external_account_type:
					v1_ExternalAccountLinkSetup_SubmittedProperties?.ExternalAccountType?.bank,
			})
		);
	}, []);

	return (
		<>
			{status === 'intro' ? (
				<LinkBankMicrosIntro next={() => setStatus('link')} linkInstantly={useBankLink} />
			) : status === 'link' ? (
				<>
					<LinkBankMicros
						onSubmit={onSubmit}
						submitting={submitting}
						onValidationErrors={handleValidationErrors}
						onBeforeValidation={handleOnBeforeValidation}
					/>
				</>
			) : status === 'restricted' ? (
				<LinkBankRestricted linkType="MicroDeposit" />
			) : null}
		</>
	);
};

LinkBankMicrosContainer.propTypes = {
	useBankLink: PropTypes.func,
	complete: PropTypes.func,
};

export default LinkBankMicrosContainer;
